import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, X, Search, Info, ChevronDown, ChevronUp } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { apiGet, apiPost } from '../utils/api';
import toast from 'react-hot-toast';
import SearchableDropdown from '../components/SearchableDropdown';

const CreateInvoice = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceData, setInvoiceData] = useState({
    invoice_date: '',
    invoice_number: '',
    due_date: '',
    notes: '',
    // FBR-specific fields
    invoice_type: 'Sale Invoice',
    scenario_id: 'SN001',
    pos_id: '',
    payment_method: 'Cash',
    buyer_province: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // FBR-related state
  const [fbrData, setFbrData] = useState({
    uom: [],
    taxRates: [],
    sroSchedules: [],
    sroItems: [],
    provinces: [],
    transactionTypes: [],
    hsCodes: []
  });
  const [expandedItems, setExpandedItems] = useState({});
  const [loadingFbrData, setLoadingFbrData] = useState(false);
  
  // Cache for FBR API responses to avoid repeated calls
  const [fbrCache, setFbrCache] = useState({
    taxRates: new Map(),
    sroSchedules: new Map(),
    sroItems: new Map()
  });
  
  // Debounce timers for API calls
  const [debounceTimers, setDebounceTimers] = useState({});

  // Helper function to format date for FBR APIs (DD-MonthNameYYYY format)
  const formatDateForFbr = (date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}${year}`;
  };

  // Debounced API call helper
  const debouncedApiCall = (key, fn, delay = 300) => {
    return new Promise((resolve) => {
      // Clear existing timer
      if (debounceTimers[key]) {
        clearTimeout(debounceTimers[key]);
      }
      
      // Set new timer
      const timer = setTimeout(async () => {
        const result = await fn();
        resolve(result);
      }, delay);
      
      setDebounceTimers(prev => ({
        ...prev,
        [key]: timer
      }));
    });
  };

  useEffect(() => {
    fetchClients();
    fetchProducts();
    fetchFbrData();
  }, []);

  // Refresh products when component gains focus (e.g., when user navigates back from products page)
  useEffect(() => {
    const handleFocus = () => {
      refreshProducts();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);


  const fetchClients = async () => {
    try {
      // limit=100 is the backend's max page size. Tenants with more than
      // that still get full coverage via searchClients below, which hits
      // the server (not just this initial page) as the user types.
      const response = await apiGet('/api/clients/?limit=100');
      if (response.ok) {
        setClients(response.data.clients || []);
      } else {
        console.error('Error fetching clients:', response.data);
        toast.error('Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to fetch clients');
    }
  };

  const fetchProducts = async () => {
    try {
      // See fetchClients above - same reasoning for the limit.
      const response = await apiGet('/api/inventory/products?limit=100');
      if (response.ok) {
        setProducts(response.data.products || []);
        console.log('✅ Products fetched:', response.data.products?.length || 0, 'products');
      } else {
        console.error('Error fetching products:', response.data);
        toast.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    }
  };

  // Merges (never replaces) so clearing the search box still shows
  // everything seen so far, since SearchableDropdown falls back to local
  // filtering over `clients`/`products` once the search term is cleared.
  // useCallback with an empty dep array keeps this reference stable across
  // renders. SearchableDropdown re-fires its debounced search whenever the
  // onSearch prop identity changes, so a plain (non-memoized) function here
  // would recreate itself every time setClients below causes a re-render -
  // triggering another search, another re-render, forever.
  const searchClients = useCallback(async (term) => {
    try {
      const response = await apiGet(`/api/clients/?search=${encodeURIComponent(term)}&limit=50`);
      if (response.ok) {
        const found = response.data.clients || [];
        setClients(prev => {
          const byId = new Map(prev.map(c => [c.id, c]));
          found.forEach(c => byId.set(c.id, c));
          return Array.from(byId.values());
        });
      }
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  }, []);

  const searchProducts = useCallback(async (term) => {
    try {
      const response = await apiGet(`/api/inventory/search?q=${encodeURIComponent(term)}&limit=50`);
      if (response.ok) {
        const found = response.data.products || [];
        setProducts(prev => {
          const byId = new Map(prev.map(p => [p.id, p]));
          found.forEach(p => byId.set(p.id, p));
          return Array.from(byId.values());
        });
      }
    } catch (error) {
      console.error('Error searching products:', error);
    }
  }, []);

  const refreshProducts = async () => {
    console.log('🔄 Refreshing products...');
    await fetchProducts();
  };

  const fetchFbrData = async () => {
    try {
      setLoadingFbrData(true);
      console.log('🔄 Fetching FBR data...');
      
      // Fetch all FBR data in parallel (same as product form)
      const [uomRes, hsCodesRes, provincesRes, transactionTypesRes] = await Promise.all([
        apiGet('/api/fbr/uom'),
        apiGet('/api/fbr/hs-codes'),
        apiGet('/api/fbr/provinces'),
        apiGet('/api/fbr/transaction-types')
      ]);

      setFbrData(prev => ({
        ...prev,
        uom: uomRes.data || [],
        hsCodes: hsCodesRes.data || [],
        provinces: provincesRes.data || [],
        transactionTypes: transactionTypesRes.data || []
      }));

      console.log('✅ FBR data loaded successfully');
    } catch (error) {
      console.error('❌ Error fetching FBR data:', error);
    } finally {
      setLoadingFbrData(false);
    }
  };

  // FBR cascading dropdown functions
  const fetchTaxRatesForSaleType = async (saleTypeId, provinceId) => {
    if (!saleTypeId || !provinceId) return [];
    
    // Check cache first
    const cacheKey = `${saleTypeId}-${provinceId}`;
    if (fbrCache.taxRates.has(cacheKey)) {
      console.log('📦 Using cached tax rates for:', cacheKey);
      return fbrCache.taxRates.get(cacheKey);
    }
    
    try {
      const today = formatDateForFbr(new Date());
      const response = await apiGet(`/api/fbr/tax-rates-for-sale-type?trans_type_id=${saleTypeId}&origination_supplier=${provinceId}&date=${today}`);
      const data = response.ok ? (response.data || []) : [];
      
      // Cache the result
      setFbrCache(prev => ({
        ...prev,
        taxRates: new Map(prev.taxRates).set(cacheKey, data)
      }));
      
      return data;
    } catch (error) {
      console.error('Error fetching tax rates:', error);
      return [];
    }
  };

  const fetchSroSchedulesForRate = async (rateId, provinceId) => {
    if (!rateId || !provinceId) return [];
    
    // Check cache first
    const cacheKey = `${rateId}-${provinceId}`;
    if (fbrCache.sroSchedules.has(cacheKey)) {
      console.log('📦 Using cached SRO schedules for:', cacheKey);
      return fbrCache.sroSchedules.get(cacheKey);
    }
    
    try {
      const today = formatDateForFbr(new Date());
      const response = await apiGet(`/api/fbr/sro-schedules-for-rate?rate_id=${rateId}&origination_supplier=${provinceId}&date=${today}`);
      const data = response.ok ? (response.data || []) : [];
      
      // Cache the result
      setFbrCache(prev => ({
        ...prev,
        sroSchedules: new Map(prev.sroSchedules).set(cacheKey, data)
      }));
      
      return data;
    } catch (error) {
      console.error('Error fetching SRO schedules:', error);
      return [];
    }
  };

  const fetchSroItemsForSchedule = async (sroId) => {
    if (!sroId) return [];
    
    // Check cache first
    if (fbrCache.sroItems.has(sroId)) {
      console.log('📦 Using cached SRO items for:', sroId);
      return fbrCache.sroItems.get(sroId);
    }
    
    try {
      const today = new Date().toISOString().split('T')[0]; // API 10 uses YYYY-MM-DD format
      const response = await apiGet(`/api/fbr/sro-items-for-schedule?date=${today}&sro_id=${sroId}`);
      const data = response.ok ? (response.data || []) : [];
      
      // Cache the result
      setFbrCache(prev => ({
        ...prev,
        sroItems: new Map(prev.sroItems).set(sroId, data)
      }));
      
      return data;
    } catch (error) {
      console.error('Error fetching SRO items:', error);
      return [];
    }
  };

  const fetchUomForHsCode = async (hsCode) => {
    if (!hsCode) return [];
    try {
      const response = await apiGet(`/api/fbr/uom-for-hs-code?hs_code=${hsCode}&annexure_id=3`);
      return response.ok ? (response.data || []) : [];
    } catch (error) {
      console.error('Error fetching UOM for HS code:', error);
      return [];
    }
  };


  const addInvoiceItem = () => {
    setInvoiceItems(prev => [...prev, {
      id: Date.now(), // Temporary ID for frontend
      product_id: '',
      product_name: '',
      quantity: 1,
      unit_price: 0,
      discount: 0,
      discount_type: 'percentage', // 'percentage' or 'fixed'
      subtotal: 0,
      tax_amount: 0,
      total: 0,
      // FBR fields
      hs_code: '',
      hs_code_description: '',
      product_description: '',
      rate: '0%',
      uom: 'Numbers, pieces, units',
      // Secondary unit (display only, computed from the product's factor)
      secondary_uom: null,
      conversion_factor: null,
      secondary_quantity: null,
      sale_type: 'Goods at standard rate (default)',
      sro_schedule_no: '',
      sro_item_serial_no: '',
      fixed_notified_value: 0,
      sales_tax_withheld: 0,
      extra_tax: 0,
      further_tax: 0,
      fed_payable: 0,
      // FBR IDs for dropdowns
      sale_type_id: null,
      rate_id: null,
      uom_id: null,
      sro_schedule_id: null,
      sro_item_id: null,
      province_id: null,
      // FBR descriptions
      sale_type_desc: null,
      rate_value: null,
      uom_desc: null,
      sro_schedule_desc: null,
      sro_item_desc: null,
      province_desc: null
    }]);
  };

  const removeInvoiceItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateInvoiceItem = (index, field, value) => {
    setInvoiceItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      const quantity = Math.max(0, parseFloat(updated[index].quantity) || 0);
      const taxRate = updated[index].rate_value !== undefined && updated[index].rate_value !== null ? Number(updated[index].rate_value) : 0;

      if (['quantity', 'unit_price', 'discount', 'discount_type'].includes(field)) {
        const unitPrice = Number(updated[index].unit_price) || 0;
        const discount = Number(updated[index].discount) || 0;
        const discountType = updated[index].discount_type || 'percentage';
        const rawSubtotal = quantity * unitPrice;
        const discountAmount = discountType === 'percentage' ? (rawSubtotal * discount) / 100 : discount;
        const finalSubtotal = Math.max(0, rawSubtotal - discountAmount);
        const taxAmount = (finalSubtotal * taxRate) / 100;
        const total = finalSubtotal + taxAmount;
        updated[index].subtotal = finalSubtotal;
        updated[index].tax_amount = taxAmount;
        updated[index].total = total;
      } else if (field === 'subtotal') {
        const salesValueExclTax = Number(value) || 0;
        updated[index].unit_price = quantity > 0 ? salesValueExclTax / quantity : 0;
        updated[index].discount = 0;
        const finalSubtotal = salesValueExclTax;
        const taxAmount = (finalSubtotal * taxRate) / 100;
        updated[index].tax_amount = taxAmount;
        updated[index].total = finalSubtotal + taxAmount;
        updated[index].subtotal = value;
      } else if (field === 'rate_value') {
        const finalSubtotal = Number(updated[index].subtotal) || 0;
        const taxAmount = (finalSubtotal * taxRate) / 100;
        updated[index].tax_amount = taxAmount;
        updated[index].total = finalSubtotal + taxAmount;
      }

      // Secondary quantity is display only; recompute it from the primary quantity
      const secFactor = Number(updated[index].conversion_factor) || 0;
      if (updated[index].secondary_uom && secFactor > 0) {
        updated[index].secondary_quantity = quantity * secFactor;
      } else {
        updated[index].secondary_quantity = null;
      }
      return updated;
    });
  };

  const selectProduct = async (product, itemIndex) => {
    updateInvoiceItem(itemIndex, 'product_id', product.id);
    updateInvoiceItem(itemIndex, 'product_name', product.name);
    updateInvoiceItem(itemIndex, 'unit_price', product.unit_price || 0);
    updateInvoiceItem(itemIndex, 'quantity', 1);
    
    // Populate FBR fields from product
    updateInvoiceItem(itemIndex, 'hs_code', product.hs_code || '');
    
    // Get HS Code description from database if not available in product
    let hsCodeDescription = product.hs_code_description || '';
    if (!hsCodeDescription && product.hs_code) {
      try {
        const hsCodeData = fbrData.hsCodes.find(hs => hs.hs_code === product.hs_code);
        if (hsCodeData) {
          hsCodeDescription = hsCodeData.description || '';
        }
      } catch (error) {
        console.error('Error fetching HS code description:', error);
      }
    }
    updateInvoiceItem(itemIndex, 'hs_code_description', hsCodeDescription);
    
    updateInvoiceItem(itemIndex, 'product_description', product.product_description || product.description || '');
    updateInvoiceItem(itemIndex, 'rate', product.rate || '0%');
    updateInvoiceItem(itemIndex, 'rate_value', product.rate_value || 0);
    updateInvoiceItem(itemIndex, 'uom', product.uom || 'Numbers, pieces, units');
    // Secondary unit carried from the product (display only). Set factor first so
    // the secondary quantity recomputes on the last call below.
    updateInvoiceItem(itemIndex, 'conversion_factor', product.conversion_factor || null);
    updateInvoiceItem(itemIndex, 'secondary_uom', (product.secondary_uom && product.conversion_factor) ? product.secondary_uom : null);
    updateInvoiceItem(itemIndex, 'sale_type', product.sale_type_desc || 'Goods at standard rate (default)');
    updateInvoiceItem(itemIndex, 'sro_schedule_no', product.sro_schedule_no || '');
    updateInvoiceItem(itemIndex, 'sro_item_serial_no', product.sro_item_serial_no || '');
    updateInvoiceItem(itemIndex, 'fixed_notified_value', product.fixed_notified_value || 0);
    updateInvoiceItem(itemIndex, 'sales_tax_withheld', product.sales_tax_withheld || 0);
    updateInvoiceItem(itemIndex, 'extra_tax', product.extra_tax || 0);
    updateInvoiceItem(itemIndex, 'further_tax', product.further_tax || 0);
    updateInvoiceItem(itemIndex, 'fed_payable', product.fed_payable || 0);
    
    // Set FBR IDs and descriptions
    updateInvoiceItem(itemIndex, 'sale_type_id', product.sale_type_id);
    updateInvoiceItem(itemIndex, 'rate_id', product.rate_id);
    updateInvoiceItem(itemIndex, 'uom_id', product.uom_id);
    updateInvoiceItem(itemIndex, 'sro_schedule_id', product.sro_schedule_id);
    updateInvoiceItem(itemIndex, 'sro_item_id', product.sro_item_id);
    updateInvoiceItem(itemIndex, 'province_id', product.province_id);
    
    // Set FBR descriptions
    updateInvoiceItem(itemIndex, 'sale_type_desc', product.sale_type_desc);
    updateInvoiceItem(itemIndex, 'uom_desc', product.uom_desc);
    updateInvoiceItem(itemIndex, 'sro_schedule_desc', product.sro_schedule_desc);
    updateInvoiceItem(itemIndex, 'sro_item_desc', product.sro_item_desc);
    updateInvoiceItem(itemIndex, 'province_desc', product.province_desc);
    
    // Smart data population: Use product data first, call APIs only if needed
    await populateFbrDataFromProduct(product, itemIndex);
    
    // Set province field for legacy compatibility
    updateInvoiceItem(itemIndex, 'province', product.province_desc);
    
  };



  const toggleItemExpansion = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // FBR cascading dropdown handlers (copied from working product form)
  // Note: HS Code and UOM are now fixed text fields populated from product selection

  // Smart FBR data population function
  const populateFbrDataFromProduct = async (product, itemIndex) => {
    console.log('🔄 Populating FBR data from product:', product.name);
    
    // Check if product has complete FBR data
    const hasCompleteData = product.rate_id && product.sro_schedule_id && product.sro_item_id;
    
    if (hasCompleteData) {
      console.log('✅ Product has complete FBR data, using product values');
      // Product has complete data, no need to call APIs
      return;
    }
    
    // Product missing some data, call APIs to populate dropdowns
    if (product.sale_type_id && product.province_id) {
      try {
        console.log('🔄 Product missing FBR data, calling APIs...');
        
        // Fetch tax rates
        const taxRates = await fetchTaxRatesForSaleType(product.sale_type_id, product.province_id);
        setFbrData(prev => ({ ...prev, taxRates }));
        
        // Don't auto-select tax rate - let user choose
        // This prevents automatic 18% selection
      } catch (error) {
        console.error('Error populating FBR data from product:', error);
      }
    }
  };

  const handleTransactionTypeChange = async (index, transactionTypeId) => {
    // Find the selected transaction type to get both ID and description
    const selectedType = fbrData.transactionTypes.find(type => type.id === transactionTypeId);
    updateInvoiceItem(index, 'sale_type_id', transactionTypeId);
    updateInvoiceItem(index, 'sale_type', selectedType?.description || transactionTypeId);
    updateInvoiceItem(index, 'sale_type_desc', selectedType?.description || '');
    
    // Clear dependent fields
    updateInvoiceItem(index, 'rate_id', null);
    updateInvoiceItem(index, 'rate', '0%');
    updateInvoiceItem(index, 'rate_value', 0);
    updateInvoiceItem(index, 'sro_schedule_id', null);
    updateInvoiceItem(index, 'sro_schedule_no', '');
    updateInvoiceItem(index, 'sro_item_id', null);
    updateInvoiceItem(index, 'sro_item_serial_no', '');
    
    // Fetch tax rates and auto-select first value if province is selected
    const item = invoiceItems[index];
    if (transactionTypeId && item.province_id) {
      try {
        const taxRates = await fetchTaxRatesForSaleType(transactionTypeId, item.province_id);
        setFbrData(prev => ({ ...prev, taxRates }));
        
        // Don't auto-select tax rate - let user choose
        // This prevents automatic 18% selection
      } catch (error) {
        console.error('Error in transaction type change:', error);
      }
    }
  };

  const handleProvinceChange = async (index, provinceId) => {
    // Find the selected province to get both ID and description
    const selectedProvince = fbrData.provinces.find(province => province.id === provinceId);
    updateInvoiceItem(index, 'province_id', provinceId);
    updateInvoiceItem(index, 'province', selectedProvince?.description || provinceId);
    updateInvoiceItem(index, 'province_desc', selectedProvince?.description || '');
    
    // Clear dependent fields
    updateInvoiceItem(index, 'rate_id', null);
    updateInvoiceItem(index, 'rate', '0%');
    updateInvoiceItem(index, 'rate_value', 0);
    updateInvoiceItem(index, 'sro_schedule_id', null);
    updateInvoiceItem(index, 'sro_schedule_no', '');
    updateInvoiceItem(index, 'sro_item_id', null);
    updateInvoiceItem(index, 'sro_item_serial_no', '');
    
    // Fetch tax rates and auto-select first value if transaction type is selected
    const item = invoiceItems[index];
    if (provinceId && item.sale_type_id) {
      try {
        const taxRates = await fetchTaxRatesForSaleType(item.sale_type_id, provinceId);
        setFbrData(prev => ({ ...prev, taxRates }));
        
        // Don't auto-select tax rate - let user choose
        // This prevents automatic 18% selection
      } catch (error) {
        console.error('Error in province change:', error);
      }
    }
  };

  const handleTaxRateChange = async (index, rateId) => {
    // Find the selected tax rate to get both ID and value
    const selectedRate = fbrData.taxRates.find(rate => rate.id === rateId);
    updateInvoiceItem(index, 'rate_id', rateId);
    updateInvoiceItem(index, 'rate', `${selectedRate?.value || 0}%`);
    updateInvoiceItem(index, 'rate_value', selectedRate?.value || 0);
    
    // Clear dependent fields
    updateInvoiceItem(index, 'sro_schedule_id', null);
    updateInvoiceItem(index, 'sro_schedule_no', '');
    updateInvoiceItem(index, 'sro_item_id', null);
    updateInvoiceItem(index, 'sro_item_serial_no', '');
    
    // Fetch SRO schedules and auto-select first value
    const item = invoiceItems[index];
    if (rateId && item.province_id) {
      try {
        const sroSchedules = await fetchSroSchedulesForRate(rateId, item.province_id);
        setFbrData(prev => ({ ...prev, sroSchedules }));
        
        // Auto-select first SRO schedule
        if (sroSchedules.length > 0) {
          const firstSchedule = sroSchedules[0];
          updateInvoiceItem(index, 'sro_schedule_id', firstSchedule.id);
          updateInvoiceItem(index, 'sro_schedule_no', firstSchedule.description);
          updateInvoiceItem(index, 'sro_schedule_desc', firstSchedule.description);
          
          // Auto-fetch and select SRO item
          const sroItems = await fetchSroItemsForSchedule(firstSchedule.id);
          setFbrData(prev => ({ ...prev, sroItems }));
          
          if (sroItems.length > 0) {
            const firstItem = sroItems[0];
            updateInvoiceItem(index, 'sro_item_id', firstItem.id);
            updateInvoiceItem(index, 'sro_item_serial_no', firstItem.serial_no);
            updateInvoiceItem(index, 'sro_item_desc', firstItem.description);
          }
        }
      } catch (error) {
        console.error('Error in tax rate change:', error);
      }
    }
  };

  const handleSroScheduleChange = async (index, scheduleId) => {
    // Find the selected SRO schedule to get both ID and description
    const selectedSchedule = fbrData.sroSchedules.find(schedule => schedule.id === scheduleId);
    updateInvoiceItem(index, 'sro_schedule_id', scheduleId);
    updateInvoiceItem(index, 'sro_schedule_no', selectedSchedule?.description || scheduleId);
    updateInvoiceItem(index, 'sro_schedule_desc', selectedSchedule?.description || '');
    
    // Clear dependent fields
    updateInvoiceItem(index, 'sro_item_id', null);
    updateInvoiceItem(index, 'sro_item_serial_no', '');
    updateInvoiceItem(index, 'sro_item_desc', '');
    
    // Fetch SRO items and auto-select first value
    if (scheduleId) {
      try {
        const sroItems = await fetchSroItemsForSchedule(scheduleId);
        setFbrData(prev => ({ ...prev, sroItems }));
        
        // Auto-select first SRO item
        if (sroItems.length > 0) {
          const firstItem = sroItems[0];
          updateInvoiceItem(index, 'sro_item_id', firstItem.id);
          updateInvoiceItem(index, 'sro_item_serial_no', firstItem.serial_no);
          updateInvoiceItem(index, 'sro_item_desc', firstItem.description);
        }
      } catch (error) {
        console.error('Error in SRO schedule change:', error);
      }
    }
  };

  const calculateTotals = () => {
    const subtotal = invoiceItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const taxAmount = invoiceItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedClient) {
      newErrors.client = 'Please select a client';
    }
    
    if (invoiceItems.length === 0) {
      newErrors.items = 'Please add at least one product';
    }
    
    // Validate each item: require positive sales value (blocks submit when unit price or sales value exc tax is empty)
    invoiceItems.forEach((item, index) => {
      if (!item.product_id) {
        newErrors[`item_${index}_product`] = 'Please select a product';
      }
      if (!item.quantity || item.quantity < 0.0001) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be at least 0.0001';
      }
      const subtotal = item.subtotal;
      const hasValidAmount = subtotal != null && subtotal !== '' && !Number.isNaN(Number(subtotal)) && Number(subtotal) > 0;
      if (!hasValidAmount) {
        newErrors[`item_${index}_price`] = 'Unit price / Sales value (Exc Tax) is required and must be greater than 0';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        client_id: parseInt(selectedClient),
        items: invoiceItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: Number(item.unit_price) || 0,
          discount: item.discount || 0,
          discount_type: item.discount_type || 'percentage',
          value_sales_excluding_st: item.subtotal != null && item.subtotal !== '' ? Number(item.subtotal) : undefined,
          // FBR fields
          hs_code: item.hs_code,
          product_description: item.product_description,
          rate: item.rate,
          rate_value: item.rate_value,
          uom: item.uom,
          sale_type: item.sale_type,
          sro_schedule_no: item.sro_schedule_no,
          sro_item_serial_no: item.sro_item_serial_no,
          fixed_notified_value: item.fixed_notified_value,
          sales_tax_withheld: item.sales_tax_withheld,
          extra_tax: item.extra_tax,
          further_tax: item.further_tax,
          fed_payable: item.fed_payable
        })),
        invoice_date: invoiceData.invoice_date ? new Date(invoiceData.invoice_date).toISOString() : null,
        invoice_number: invoiceData.invoice_number || null,
        due_date: invoiceData.due_date ? new Date(invoiceData.due_date).toISOString() : null,
        notes: invoiceData.notes,
        invoice_type: invoiceData.invoice_type,
        scenario_id: invoiceData.scenario_id,
        pos_id: invoiceData.pos_id,
        payment_method: invoiceData.payment_method,
        buyer_province: invoiceData.buyer_province || null
      };

      const response = await apiPost('/api/invoices/', payload);
      
      if (response.ok) {
        // Automatically validate invoice with FBR in validation mode
        const newInvoice = response.data.invoice;
        if (newInvoice && newInvoice.id) {
          try {
            await apiPost(`/api/invoices/${newInvoice.id}/submit-to-fbr?mode=validation`, {});
          } catch (fbrErr) {
            console.error("Auto FBR validation error:", fbrErr);
          }
        }

        toast.success('Invoice created successfully! Redirecting to invoices...');
        // Reset form
        setInvoiceItems([]);
        setSelectedClient('');
        setInvoiceData({
          invoice_date: '',
          invoice_number: '',
          due_date: '',
          notes: '',
          invoice_type: 'Sale Invoice',
          scenario_id: 'SN001',
          pos_id: '',
          payment_method: 'Cash'
        });
        setErrors({});
        
        // Redirect to invoices page after a short delay
        setTimeout(() => {
          window.location.href = '/invoices';
        }, 1500);
      } else {
        toast.error(response.data.detail || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Error creating invoice');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <Navigation>
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Create Invoice</h1>
                <p className="text-slate-600">Create a new invoice with multiple products</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Client Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Client Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Client <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={clients.map(client => ({
                      id: client.id,
                      description: `${client.name} - ${client.email || client.ntn_cnic}`
                    }))}
                    value={selectedClient}
                    onChange={(value) => {
                      setSelectedClient(value);
                      const client = clients.find(c => c.id === parseInt(value));
                      if (client && client.province) {
                        setInvoiceData(prev => ({ ...prev, buyer_province: client.province }));
                      } else {
                        setInvoiceData(prev => ({ ...prev, buyer_province: '' }));
                      }
                    }}
                    placeholder="Search clients..."
                    className={`${errors.client ? 'border-red-500' : 'border-slate-300'}`}
                    onSearch={searchClients}
                  />
                  {errors.client && (
                    <p className="text-red-500 text-sm mt-1">{errors.client}</p>
                  )}
                </div>

                {selectedClient && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Buyer Province (FBR Destination)
                    </label>
                    <select
                      className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm p-2 border"
                      value={invoiceData.buyer_province}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, buyer_province: e.target.value }))}
                    >
                      <option value="">Select Province</option>
                      {fbrData.provinces.map((province) => (
                        <option key={province.id} value={province.description}>
                          {province.description}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-slate-900">Invoice Items</h3>
                <button
                  type="button"
                  onClick={addInvoiceItem}
                  className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-800 flex items-center shadow-sm hover:shadow-md transition-all"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </button>
              </div>

              {errors.items && (
                <p className="text-red-500 text-sm mb-4">{errors.items}</p>
              )}

              {invoiceItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Info className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                  <p>No products added yet. Click "Add Product" to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {invoiceItems.map((item, index) => (
                    <div key={item.id} className="border rounded-lg p-3 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {/* Product Selection with Delete Button */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Product <span className="text-red-500">*</span>
                          </label>
                          <SearchableDropdown
                            options={products.map(product => ({
                              id: product.id,
                              description: `${product.name} - SKU: ${product.sku} - Rs. ${product.unit_price?.toLocaleString() || 0}`,
                              product_name: product.name,
                              sku: product.sku,
                              unit_price: product.unit_price,
                              stock_quantity: product.stock_quantity
                            }))}
                            value={item.product_id}
                            onChange={async (value, option) => {
                              const selectedProduct = products.find(p => p.id === value);
                              if (selectedProduct) {
                                await selectProduct(selectedProduct, index);
                              }
                            }}
                            placeholder="Search products..."
                            className={`text-sm ${errors[`item_${index}_product`] ? 'border-red-500' : 'border-slate-300'}`}
                            dropdownWidth="w-96"
                            displayFormat="detailed"
                            selectedDisplayKey="product_name"
                            onSearch={searchProducts}
                          />
                          {errors[`item_${index}_product`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_product`]}</p>
                          )}
                        </div>

                        {/* Quantity (label carries the primary unit) */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {item.uom ? `Quantity (${item.uom})` : 'Quantity'} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            value={item.quantity === '' || item.quantity == null ? '' : String(item.quantity)}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d{0,4}$/.test(value)) {
                                updateInvoiceItem(index, 'quantity', value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value !== '' && !isNaN(parseFloat(value)) && parseFloat(value) >= 0) {
                                updateInvoiceItem(index, 'quantity', parseFloat(value));
                              }
                            }}
                            className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white ${
                              errors[`item_${index}_quantity`] ? 'border-red-500' : 'border-slate-300'
                            }`}
                          />
                          {errors[`item_${index}_quantity`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_quantity`]}</p>
                          )}
                          {item.secondary_uom && item.secondary_quantity != null && (
                            <p className="text-xs text-amber-700 mt-1">
                              = {Number(item.secondary_quantity).toLocaleString(undefined, { maximumFractionDigits: 4 })} {item.secondary_uom} (auto)
                            </p>
                          )}
                        </div>

                        {/* Unit Price - allow empty, up to 4 decimals; validation blocks submit if empty */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Unit Price <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            value={item.unit_price === '' || item.unit_price == null ? '' : String(item.unit_price)}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d{0,4}$/.test(value)) {
                                updateInvoiceItem(index, 'unit_price', value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value !== '' && !isNaN(parseFloat(value)) && parseFloat(value) >= 0) {
                                updateInvoiceItem(index, 'unit_price', parseFloat(value));
                              }
                            }}
                            className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white ${
                              errors[`item_${index}_price`] ? 'border-red-500' : 'border-slate-300'
                            }`}
                            placeholder="0.0000"
                          />
                          {errors[`item_${index}_price`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_price`]}</p>
                          )}
                        </div>

                        {/* Discount */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Discount
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            value={item.discount === '' || item.discount == null ? '' : String(item.discount)}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                updateInvoiceItem(index, 'discount', value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value !== '' && !isNaN(parseFloat(value)) && parseFloat(value) >= 0) {
                                updateInvoiceItem(index, 'discount', parseFloat(value));
                              }
                            }}
                            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                          />
                      </div>

                        {/* Discount Type and Delete Button */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Type
                          </label>
                          <div className="flex gap-1">
                            <select
                              value={item.discount_type}
                              onChange={(e) => updateInvoiceItem(index, 'discount_type', e.target.value)}
                              className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            >
                              <option value="percentage">%</option>
                              <option value="fixed">Rs</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => removeInvoiceItem(index)}
                              className="px-3 py-1.5 text-red-600 hover:text-red-800 border border-red-300 rounded-lg hover:bg-red-50 flex items-center justify-center"
                              title="Remove item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                      </div>
                        </div>
                      </div>

                      {/* Item Totals Breakdown */}
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="grid grid-cols-3 gap-3 items-end">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Subtotal (Exc Tax)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              value={item.subtotal === '' || item.subtotal == null ? '' : String(item.subtotal)}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^\d*\.?\d{0,6}$/.test(value)) {
                                  updateInvoiceItem(index, 'subtotal', value);
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value !== '' && !isNaN(parseFloat(value)) && parseFloat(value) >= 0) {
                                  updateInvoiceItem(index, 'subtotal', parseFloat(value));
                                }
                              }}
                              className="w-full px-3 py-2 text-sm font-semibold border-2 border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Tax Amount ({item.rate_value ?? 0}%)</label>
                            <div className="w-full px-3 py-2 text-sm font-semibold border-2 border-slate-200 rounded-lg bg-slate-50 text-slate-700">
                              {(item.tax_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Total (Inc Tax)</label>
                            <div className="w-full px-3 py-2 text-sm font-bold text-amber-700 bg-amber-50 border-2 border-amber-200 rounded-lg">
                              Rs. {(item.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* FBR Fields Toggle Button */}
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={() => toggleItemExpansion(item.id)}
                          className="flex items-center justify-between w-full text-left text-sm font-medium text-amber-600 hover:text-amber-800"
                        >
                          <span>Edit FBR Fields</span>
                          {expandedItems[item.id] ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Collapsible FBR Fields */}
                      {expandedItems[item.id] && (
                        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-amber-900 mb-2">FBR Compliance Fields</h5>
                            <div className="text-xs text-slate-600">
                              <span className="font-medium">Product Info:</span> {item.hs_code || 'Not set'} - {item.hs_code_description || 'No description'} - {item.uom || 'Not set'}
                            </div>
                          </div>
                          
                          {/* Row 1: Sale Type and Province */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Transaction Type</label>
                              <SearchableDropdown
                                options={fbrData.transactionTypes.map(type => ({
                                  id: type.id,
                                  description: type.description
                                }))}
                                value={item.sale_type_id}
                                onChange={(value) => handleTransactionTypeChange(index, value)}
                                placeholder="Select Transaction Type"
                                displayKey="description"
                                valueKey="id"
                                className="text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Province</label>
                              <SearchableDropdown
                                options={fbrData.provinces.map(province => ({
                                  id: province.id,
                                  description: province.description
                                }))}
                                value={item.province_id}
                                onChange={(value) => handleProvinceChange(index, value)}
                                placeholder="Select Province"
                                displayKey="description"
                                valueKey="id"
                                className="text-sm"
                              />
                            </div>
                          </div>

                          {/* Row 2: Tax Rate and SRO Schedule */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Tax Rate</label>
                              <SearchableDropdown
                                options={fbrData.taxRates.map(rate => ({
                                  id: rate.id,
                                  description: `${rate.description} (${rate.value}%)`
                                }))}
                                value={item.rate_id}
                                onChange={(value) => handleTaxRateChange(index, value)}
                                placeholder="Select Tax Rate"
                                displayKey="description"
                                valueKey="id"
                                className="text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">SRO Schedule</label>
                              <SearchableDropdown
                                options={fbrData.sroSchedules.map(schedule => ({
                                  id: schedule.id,
                                  description: schedule.description
                                }))}
                                value={item.sro_schedule_id}
                                onChange={(value) => handleSroScheduleChange(index, value)}
                                placeholder="Select SRO Schedule"
                                displayKey="description"
                                valueKey="id"
                                className="text-sm"
                              />
                            </div>
                          </div>

                          {/* Row 3: SRO Item and Description */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">SRO Item</label>
                              <SearchableDropdown
                                options={fbrData.sroItems.map(item => ({
                                  id: item.id,
                                  description: `${item.description} (${item.id})`
                                }))}
                                value={item.sro_item_id}
                                onChange={(value) => {
                                  const selectedItem = fbrData.sroItems.find(sroItem => sroItem.id === value);
                                  updateInvoiceItem(index, 'sro_item_id', value);
                                  updateInvoiceItem(index, 'sro_item_serial_no', selectedItem?.description || value);
                                  updateInvoiceItem(index, 'sro_item_desc', selectedItem?.description || '');
                                }}
                                placeholder={item.sro_schedule_id ? "Select SRO Item" : "Select SRO Schedule first"}
                                disabled={!item.sro_schedule_id}
                                displayKey="description"
                                valueKey="id"
                                className="text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Product Description</label>
                              <input
                                type="text"
                                value={item.product_description}
                                onChange={(e) => updateInvoiceItem(index, 'product_description', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                placeholder="Detailed product description"
                              />
                            </div>
                          </div>

                          {/* Row 5: Additional Tax Fields */}
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Fixed Notified Value</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.fixed_notified_value}
                                onChange={(e) => updateInvoiceItem(index, 'fixed_notified_value', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Sales Tax Withheld</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.sales_tax_withheld}
                                onChange={(e) => updateInvoiceItem(index, 'sales_tax_withheld', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Extra Tax</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.extra_tax}
                                onChange={(e) => updateInvoiceItem(index, 'extra_tax', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Further Tax</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.further_tax}
                                onChange={(e) => updateInvoiceItem(index, 'further_tax', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Federal Tax Payable (Rs.)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.fed_payable}
                                onChange={(e) => updateInvoiceItem(index, 'fed_payable', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invoice Summary - Always Visible */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Invoice Summary</h3>
              <div className="space-y-4">
                {/* Calculated Tax Amount and Total */}
                {invoiceItems.length > 0 && (
                  <div className="space-y-2 text-right border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Tax Amount:</span>
                      <span className="font-medium">Rs. {taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between">
                        <span className="text-lg font-medium text-slate-900">Total:</span>
                        <span className="text-lg font-bold text-amber-700">Rs. {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Additional Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceData.invoice_date}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty to use current date</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Invoice Number</label>
                  <input
                    type="text"
                    value={invoiceData.invoice_number}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, invoice_number: e.target.value }))}
                    placeholder="Auto-generated if empty"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty to auto-generate</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={invoiceData.due_date}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Payment Method</label>
                  <select
                    value={invoiceData.payment_method}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Check">Check</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={invoiceData.notes}
                  onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading || invoiceItems.length === 0}
                className="bg-slate-700 text-white px-6 py-3 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm hover:shadow-md transition-all"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Invoice...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Invoice
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Product Search Modal */}
        {/* This section is no longer needed as product search is inline */}
      </div>
    </Navigation>
  );
};

export default CreateInvoice; 